import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { certificateSettingsAPI } from '../../services/api';
import { toast } from 'react-toastify';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
`;

const Section = styled(Card)`
  padding: 1.5rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 0.75rem;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border.medium};
  font-family: inherit;
  font-size: 0.9rem;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const UploadPreview = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 0.75rem;

  img {
    width: 120px;
    height: auto;
    border: 1px solid ${props => props.theme.colors.border.light};
    border-radius: ${props => props.theme.borderRadius.sm};
    object-fit: contain;
    background: ${props => props.theme.colors.background.secondary};
    padding: 0.5rem;
  }
`;

const CertificatePreviewFrame = styled.div`
  width: 100%;
  background: #f8f7f1;
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid rgba(24, 35, 52, 0.08);
  padding: 24px;
  display: flex;
  justify-content: center;
`;

const CertificateCanvas = styled.div`
  width: min(520px, 100%);
  background: #dfd7c5;
  padding: 15px;
  border: 1px solid rgba(26, 37, 55, 0.15);
`;

const CertificateFrameBorder = styled.div`
  width: 100%;
  height: 100%;
  border: 8px solid #cba74a;
  padding: 18px;
  position: relative;
  background: #f9f6ed;

  &::before {
    content: '';
    position: absolute;
    top: 18px;
    bottom: 18px;
    left: 18px;
    right: 18px;
    border: 2px solid #cba74a;
    pointer-events: none;
  }
`;

const CertificateContent = styled.div`
  position: relative;
  height: 100%;
  padding: 32px 28px 28px;
  font-family: inherit;
  color: #1a2537;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  h1 {
    letter-spacing: 0.32em;
    font-size: 28px;
    margin-bottom: 6px;
  }

  h2 {
    margin: 14px 0 6px;
    font-size: 24px;
    letter-spacing: 0.22em;
  }

  p {
    margin: 0.35rem auto;
    max-width: 420px;
    color: #4b5563;
    line-height: 1.4;
    font-size: 13px;
  }
`;

const defaults = {
  orientation: 'portrait',
  title_text: 'CERTIFICATE',
  intro_text: 'This certificate is proudly presented to',
  body_text:
    'Empowering Women Human Rights Defenders Network recognizes the unwavering courage and care shown by {{participant_name}} in advancing inclusive justice throughout Ethiopia. This honor commends the successful completion of {{training_title}} held {{training_dates}}, and celebrates the commitment to amplify community voices and protect dignity for all.',
  quote_text: '“Together we nurture safe spaces where defenders learn, heal, and lead.”',
  director_name: '',
  director_title: '',
  logo_path: null,
  signature_path: null,
  qr_enabled: true,
  font_family: 'Playfair Display',
};

const FONT_OPTIONS = [
  'Playfair Display',
  'Times New Roman',
  'Georgia',
  'Libre Baskerville',
  'Poppins',
  'Source Serif Pro',
  'Merriweather',
];

const SelectInput = styled.select`
  width: 100%;
  padding: 0.75rem;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border.medium};
  font-size: 0.9rem;
`;

const sampleRegistration = {
  full_name: 'Ms. Sample Defender',
  certificate: {
    certificate_number: 'TC-000123',
    verification_code: 'ABC123XYZ',
  },
};

const sampleTraining = {
  title: 'Digital Security Training',
  location: 'Addis Ababa, Ethiopia',
  start_date: new Date('2025-01-10'),
  end_date: new Date('2025-01-15'),
};

const CertificateSettingsPage = () => {
  const [form, setForm] = useState(defaults);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [signatureUploading, setSignatureUploading] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await certificateSettingsAPI.get();
        setForm(prev => ({ ...prev, ...data }));
      } catch (error) {
        console.error('Failed to load certificate settings:', error);
        toast.error('Unable to load certificate settings.');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await certificateSettingsAPI.update({
        orientation: form.orientation,
        title_text: form.title_text,
        intro_text: form.intro_text,
        body_text: form.body_text,
        quote_text: form.quote_text,
        director_name: form.director_name,
        director_title: form.director_title,
        font_family: form.font_family,
        qr_enabled: form.qr_enabled,
      });
      toast.success('Certificate settings saved.');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.message || 'Unable to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('logo', file);
    setLogoUploading(true);
    try {
      const { data } = await certificateSettingsAPI.uploadLogo(formData);
      setForm(prev => ({ ...prev, logo_path: data.path }));
      toast.success('Logo uploaded.');
    } catch (error) {
      console.error('Failed to upload logo:', error);
      toast.error(error.response?.data?.message || 'Unable to upload logo.');
    } finally {
      setLogoUploading(false);
      event.target.value = '';
    }
  };

  const handleSignatureUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('signature', file);
    setSignatureUploading(true);
    try {
      const { data } = await certificateSettingsAPI.uploadSignature(formData);
      setForm(prev => ({ ...prev, signature_path: data.path }));
      toast.success('Signature uploaded.');
    } catch (error) {
      console.error('Failed to upload signature:', error);
      toast.error(error.response?.data?.message || 'Unable to upload signature.');
    } finally {
      setSignatureUploading(false);
      event.target.value = '';
    }
  };

  const previewData = useMemo(() => {
    const dateSummary = `${sampleTraining.start_date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })} – ${sampleTraining.end_date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`;

    const tokens = {
      '{{participant_name}}': sampleRegistration.full_name,
      '{{training_title}}': sampleTraining.title,
      '{{training_dates}}': dateSummary,
      '{{training_location}}': sampleTraining.location,
      '{{issued_date}}': 'January 20, 2025',
      '{{organization}}': 'EWHRDN',
    };

    const replaceTokens = (text = '') =>
      text
        ? Object.keys(tokens).reduce(
            (acc, key) => acc.replaceAll(key, tokens[key]),
            text
          )
        : '';

    const previewQrPayload = JSON.stringify({
      participant: sampleRegistration.full_name,
      training: sampleTraining.title,
      dates: dateSummary,
      issued: 'January 20, 2025',
      certificate_no: sampleRegistration.certificate.certificate_number,
      verification_code: sampleRegistration.certificate.verification_code,
    });

    return {
      ...form,
      intro_text: replaceTokens(form.intro_text),
      body_text: replaceTokens(form.body_text),
      quote_text: replaceTokens(form.quote_text),
      director_name: form.director_name,
      director_title: form.director_title,
      font_family: form.font_family || 'Playfair Display',
      qr_preview_url: form.qr_enabled
        ? `https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(previewQrPayload)}`
        : null,
    };
  }, [form]);

  if (loading) {
    return <p>Loading certificate settings…</p>;
    }

  return (
    <PageContainer>
      <h1>Certificate Settings</h1>
      <p style={{ color: '#6b7280' }}>
        Configure the default layout, text, and assets that appear on every issued certificate. Use tokens such
        as <code>{'{{participant_name}}'}</code> or <code>{'{{training_title}}'}</code> to include dynamic data.
      </p>

      <Grid>
        <Section as="form" onSubmit={handleSave}>
          <h3>Layout & Content</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
            <div>
              <Label>Orientation</Label>
              <RadioGroup>
                {['portrait', 'landscape'].map((option) => (
                  <label key={option} style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                    <input
                      type="radio"
                      value={option}
                      checked={form.orientation === option}
                      onChange={(e) => handleChange('orientation', e.target.value)}
                    />
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </label>
                ))}
              </RadioGroup>
            </div>
            <Input
              label="Title"
              value={form.title_text}
              onChange={(e) => handleChange('title_text', e.target.value)}
            />
            <Input
              label="Intro Text"
              value={form.intro_text}
              onChange={(e) => handleChange('intro_text', e.target.value)}
            />
            <div>
              <Label>Font</Label>
              <SelectInput value={form.font_family || 'Playfair Display'} onChange={(e) => handleChange('font_family', e.target.value)}>
                {FONT_OPTIONS.map((font) => (
                  <option key={font} value={font}>
                    {font}
                  </option>
                ))}
              </SelectInput>
            </div>
            <div>
              <Label>Body Text</Label>
              <TextArea value={form.body_text} onChange={(e) => handleChange('body_text', e.target.value)} />
            </div>
            <div>
              <Label>Quote</Label>
              <TextArea value={form.quote_text || ''} onChange={(e) => handleChange('quote_text', e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <Input
                label="Director Name"
                value={form.director_name || ''}
                onChange={(e) => handleChange('director_name', e.target.value)}
              />
              <Input
                label="Director Title"
                value={form.director_title || ''}
                onChange={(e) => handleChange('director_title', e.target.value)}
              />
            </div>
            <label style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={form.qr_enabled}
                onChange={(e) => handleChange('qr_enabled', e.target.checked)}
              />
              Show QR code on certificate
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <Button type="submit" variant="primary" loading={saving}>
                Save Settings
              </Button>
            </div>
          </div>
        </Section>

        <Section>
          <h3>Branding Assets</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
            <div>
              <Label>Logo</Label>
              <UploadPreview>
                {form.logo_path ? (
                  <img src={`/storage/${form.logo_path}`} alt="Certificate logo" />
                ) : (
                  <div style={{ width: 120, height: 80, border: '1px dashed #cbd5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Placeholder
                  </div>
                )}
                <div>
                  <input type="file" accept="image/*" onChange={handleLogoUpload} disabled={logoUploading} />
                  <p style={{ fontSize: '0.8rem', color: '#6b7280' }}>Recommended PNG/SVG, max 4 MB.</p>
                </div>
              </UploadPreview>
            </div>
            <div>
              <Label>Director Signature</Label>
              <UploadPreview>
                {form.signature_path ? (
                  <img src={`/storage/${form.signature_path}`} alt="Director signature" />
                ) : (
                  <div style={{ width: 120, height: 80, border: '1px dashed #cbd5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Placeholder
                  </div>
                )}
                <div>
                  <input type="file" accept="image/*" onChange={handleSignatureUpload} disabled={signatureUploading} />
                  <p style={{ fontSize: '0.8rem', color: '#6b7280' }}>Transparent PNG recommended, max 4 MB.</p>
                </div>
              </UploadPreview>
            </div>
          </div>
        </Section>
      </Grid>

      <Section>
        <h3>Live Preview</h3>
        <CertificatePreviewFrame>
          <CertificateCanvas style={{ aspectRatio: previewData.orientation === 'landscape' ? '297 / 210' : '210 / 297' }}>
            <CertificateFrameBorder>
              <CertificateContent style={{ fontFamily: `${previewData.font_family || 'Playfair Display'}, 'Times New Roman', serif` }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  {form.logo_path ? (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <img src={`/storage/${form.logo_path}`} alt="Logo" style={{ width: 68, height: 'auto' }} />
                    </div>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 42 }}>
                      <span style={{ fontWeight: 700, letterSpacing: '0.32em', fontSize: '11px' }}>EWHRDN</span>
                      <span style={{ fontWeight: 700, letterSpacing: '0.32em', fontSize: '11px' }}>EWHRDN</span>
                    </div>
                  )}
                  <div>
                    <h1>{previewData.title_text || 'CERTIFICATE'}</h1>
                    <div style={{ width: 140, height: 2, background: '#cba74a', margin: '10px auto 5px' }} />
                    <div style={{ width: 110, height: 2, background: '#cba74a', margin: '6px auto 14px' }} />
                  </div>
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 14 }}>
                  <div>
                    <p style={{ letterSpacing: '0.26em', textTransform: 'uppercase', fontSize: 11, color: '#7a7d85' }}>
                      {previewData.intro_text || 'This certificate is proudly presented to'}
                    </p>
                    <h2>{sampleRegistration.full_name}</h2>
                    <div style={{ width: '52%', height: 1, background: '#d6ba72', margin: '10px auto 16px' }} />
                  </div>
                  <p style={{ fontSize: 13, lineHeight: 1.5 }}>{previewData.body_text}</p>
                  {previewData.quote_text && (
                    <p style={{ fontStyle: 'italic', color: '#5f6470', fontSize: 12, marginTop: 6 }}>{previewData.quote_text}</p>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', textAlign: 'left', gap: 18, marginTop: 10 }}>
                  <div style={{ flex: 1, fontSize: 11, color: '#6b7280', lineHeight: 1.4 }}>
                    <div>Issued on January 20, 2025</div>
                    <div>{sampleTraining.location}</div>
                    <div>Certificate No: {sampleRegistration.certificate.certificate_number}</div>
                    <div>Verification Code: {sampleRegistration.certificate.verification_code}</div>
                  </div>
                  {(form.signature_path || previewData.director_name || previewData.director_title) && (
                    <div style={{ textAlign: 'left', fontSize: 11, color: '#4d5160' }}>
                      {form.signature_path ? (
                        <img
                          src={`/storage/${form.signature_path}`}
                          alt="Director signature"
                          style={{ width: 90, height: 45, objectFit: 'contain', marginBottom: 4 }}
                        />
                      ) : (
                        <div style={{ width: 120, height: 1, background: '#cba74a', marginBottom: 4 }} />
                      )}
                      {previewData.director_name && <div>{previewData.director_name}</div>}
                      {previewData.director_title && (
                        <div style={{ fontSize: 11, color: '#6b7280' }}>{previewData.director_title}</div>
                      )}
                    </div>
                  )}
                  {previewData.qr_enabled && (
                    <div style={{ textAlign: 'center', fontSize: 11, color: '#4b5563' }}>
                      {previewData.qr_preview_url ? (
                        <img
                          src={previewData.qr_preview_url}
                          alt="QR preview"
                          style={{ width: 100, height: 100, border: '4px solid #f9f6ed', marginBottom: 4 }}
                        />
                      ) : (
                        <div style={{ width: 100, height: 100, border: '4px solid #f9f6ed', margin: '0 auto 4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                          QR
                        </div>
                      )}
                      <div>Scan to confirm certificate details</div>
                    </div>
                  )}
                </div>
              </CertificateContent>
            </CertificateFrameBorder>
          </CertificateCanvas>
        </CertificatePreviewFrame>
      </Section>
    </PageContainer>
  );
};

export default CertificateSettingsPage;

